import Swiper from 'swiper';
import { Navigation, Keyboard } from 'swiper/modules';

class PricingShowcaseCarousel extends HTMLElement {
  #tabClickHandlers = new Map();

  connectedCallback() {
    this.container = this.closest('.pricing-showcase');
    this.isCompact = this.container?.dataset.layout === 'compact';
    this.formatLabel = this.querySelector('.format-label');

    if (this.isCompact) {
      this.compactTable = this.container?.querySelector('.pricing-compact');
    } else {
      this.tableContainer = this.container?.querySelector('.pricing-showcase-table');
    }

    this.#initSwiper();
    this.#initTabs();

    if (this.isCompact) {
      this.#updateCompactTabs(0);
    } else {
      this.#initHighlight();
      this.#applyRowStyling();
    }
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    for (const [tab, handler] of this.#tabClickHandlers) {
      tab.removeEventListener('click', handler);
    }
    this.#tabClickHandlers.clear();
  }

  #initSwiper() {
    const swiperContainer = this.querySelector('.swiper');
    if (!swiperContainer) return;

    const hasNavigation = this.querySelector('.next-button') !== null;
    const modules = [Keyboard];
    if (hasNavigation) modules.push(Navigation);

    this.swiper = new Swiper(swiperContainer, {
      modules,
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      navigation: hasNavigation
        ? {
            nextEl: this.querySelector('.next-button'),
            prevEl: this.querySelector('.prev-button'),
          }
        : false,
      on: {
        slideChange: (swiper) => {
          this.#onSlideChange(swiper);
        },
      },
    });
  }

  #initTabs() {
    if (this.isCompact) {
      this.#initCompactTabs();
    } else {
      this.#initDefaultTabs();
    }
  }

  #initDefaultTabs() {
    const tabContainer = this.container?.querySelector('.pricing-showcase-tabs');
    if (!tabContainer) return;

    this.tabContainer = tabContainer;
    this.tabList = tabContainer.querySelector('[role="tablist"]');
    this.tabs = tabContainer.querySelectorAll('.format-tab');

    for (const tab of this.tabs) {
      const handler = () => {
        const index = parseInt(tab.dataset.formatIndex, 10);
        if (!Number.isNaN(index)) this.#goToSlide(index);
      };
      this.#tabClickHandlers.set(tab, handler);
      tab.addEventListener('click', handler);
    }
  }

  #initCompactTabs() {
    if (!this.compactTable) return;

    this.compactTabList = this.compactTable.querySelector('.pricing-compact__tabs');
    this.tabs = this.compactTable.querySelectorAll('.compact-tab');

    for (const tab of this.tabs) {
      const handler = () => {
        const index = parseInt(tab.dataset.formatIndex, 10);
        if (!Number.isNaN(index)) this.#goToSlide(index);
      };
      this.#tabClickHandlers.set(tab, handler);
      tab.addEventListener('click', handler);
    }
  }

  #initHighlight() {
    if (!this.tabContainer) return;

    this.highlight = this.tabContainer.querySelector('.tab-highlight');
    if (!this.highlight) return;

    const firstTab = this.tabContainer.querySelector('[aria-selected="true"]');

    if (firstTab) {
      this.#updateHighlight(firstTab);
      setTimeout(() => {
        this.highlight.classList.add('transition-all', 'duration-300');
      }, 250);
    }
  }

  #updateHighlight(activeTab) {
    if (!this.highlight || !activeTab) return;

    const tabList = this.highlight.parentElement;
    const { left, width, height } = activeTab.getBoundingClientRect();
    const containerLeft = tabList.getBoundingClientRect().left;
    const paddingLeft = parseInt(getComputedStyle(tabList).paddingLeft, 10);

    Object.assign(this.highlight.style, {
      width: `${width}px`,
      height: `${height}px`,
      transform: `translateX(${left - containerLeft + tabList.scrollLeft - paddingLeft}px)`,
    });
  }

  #onSlideChange(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return;

    const formatGroup = activeSlide.dataset.formatGroup;
    const formatLabel = activeSlide.dataset.formatLabel;

    if (this.formatLabel && formatLabel) {
      this.formatLabel.textContent = formatLabel;
    }

    if (this.isCompact) {
      this.#updateCompactTabs(swiper.realIndex);
      this.#filterCompactVariants(formatGroup);
    } else {
      this.#updateTabs(swiper.realIndex);
      this.#filterPricingTable(formatGroup);
    }
  }

  #updateTabs(activeIndex) {
    if (!this.tabs) return;

    let index = 0;
    let activeTab = null;

    for (const tab of this.tabs) {
      const isActive = index === activeIndex;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');

      if (isActive) {
        activeTab = tab;
        this.#updateHighlight(tab);
      }
      index++;
    }

    if (activeTab) {
      this.#scrollTabIntoView(activeTab, this.tabList);
    }
  }

  #updateCompactTabs(activeIndex) {
    if (!this.tabs) return;

    for (const tab of this.tabs) {
      const index = parseInt(tab.dataset.formatIndex, 10);
      const isActive = index === activeIndex;

      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');

      if (isActive) {
        tab.classList.add('bg-white', 'text-gray-900');
        tab.classList.remove('bg-gray-600', 'text-white', 'hover:bg-gray-500');
        this.#scrollTabIntoView(tab, this.compactTabList);
      } else {
        tab.classList.remove('bg-white', 'text-gray-900');
        tab.classList.add('bg-gray-600', 'text-white', 'hover:bg-gray-500');
      }
    }
  }

  #filterCompactVariants(formatGroup) {
    if (!this.compactTable) return;

    const dataContainer = this.compactTable.querySelector('.pricing-compact__data');
    if (!dataContainer) return;

    const columns = dataContainer.querySelectorAll('.compact-variant-col');

    for (const col of columns) {
      const colGroups = (col.dataset.formatGroup || '').split(',').map((g) => g.trim());
      const isMatch = colGroups.includes(formatGroup);
      col.classList.toggle('hidden', !isMatch);
    }

    dataContainer.scrollTo({ left: 0 });
  }

  #scrollTabIntoView(tab, tabList) {
    if (!tabList || !tab) return;

    const { scrollLeft, clientWidth } = tabList;
    const tabRect = tab.getBoundingClientRect();
    const containerRect = tabList.getBoundingClientRect();
    const tabLeft = tabRect.left - containerRect.left + scrollLeft;
    const tabCenter = tabLeft + tabRect.width / 2;
    const containerCenter = clientWidth / 2;
    const newScrollLeft = tabCenter - containerCenter;

    if (Math.abs(newScrollLeft - scrollLeft) > 5) {
      tabList.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  }

  #goToSlide(index) {
    if (!this.swiper) return;
    this.swiper.slideTo(index);
  }

  #filterPricingTable(formatGroup) {
    this.tableContainer =
      this.closest('.pricing-showcase')?.querySelector('.pricing-showcase-table');
    if (!this.tableContainer) return;

    const rows = this.tableContainer.querySelectorAll('.pricing-row');

    for (const row of rows) {
      const rowGroups = (row.dataset.formatGroup || '').split(',').map((g) => g.trim());
      const isMatch = rowGroups.includes(formatGroup);
      row.classList.toggle('hidden', !isMatch);
    }

    this.#applyRowStyling();
  }

  #applyRowStyling() {
    if (!this.tableContainer) return;

    const visibleRows = this.tableContainer.querySelectorAll('.pricing-row:not(.hidden)');
    let index = 0;

    for (const row of visibleRows) {
      const offerCell = row.querySelector('.offer-cell');
      const isEven = index % 2 === 0;
      const isLast = index === visibleRows.length - 1;

      row.classList.remove('bg-white', 'bg-gray-50');

      if (!offerCell) {
        index++;
        continue;
      }

      offerCell.classList.remove('bg-gray-200', 'bg-gray-300', 'rounded-b-xl');
      row.classList.add(isEven ? 'bg-white' : 'bg-gray-50');
      offerCell.classList.add(isEven ? 'bg-gray-300' : 'bg-gray-200');

      if (isLast) offerCell.classList.add('rounded-b-xl');
      index++;
    }
  }
}

customElements.define('pricing-showcase-carousel', PricingShowcaseCarousel);
